/**
 * Returns an array of unique objects based on a specified key property.
 * When duplicate keys are found, the last occurrence is kept.
 *
 * @template T - The type of objects in the array
 * @param {T[]} array - The array of objects to filter for uniqueness
 * @param {keyof T} key - The property key to use for uniqueness comparison
 * @returns {T[]} A new array containing only unique objects based on the specified key
 *
 * @example
 * ```typescript
 * interface User {
 *   id: number;
 *   name: string;
 *   email: string;
 * }
 *
 * const users: User[] = [
 *   { id: 1, name: 'John', email: 'john@email.com' },
 *   { id: 2, name: 'Jane', email: 'jane@email.com' },
 *   { id: 1, name: 'John Updated', email: 'john.new@email.com' },
 *   { id: 3, name: 'Bob', email: 'bob@email.com' }
 * ];
 *
 * const uniqueUsers = getUniqueByKey(users, 'id');
 * Result: [
 *   { id: 2, name: 'Jane', email: 'jane@email.com' },
 *   { id: 1, name: 'John Updated', email: 'john.new@email.com' }, // Last occurrence kept
 *   { id: 3, name: 'Bob', email: 'bob@email.com' }
 * ]
 *
 * const uniqueByEmail = getUniqueByKey(users, 'email');
 * All objects would be unique since emails are different
 * ```
 */
export function getUniqueByKey<T>(array: T[], key: keyof T): T[] {
  return [...new Map(array.map((item) => [item[key], item])).values()];
}
